import React from "react"
import {useSearchParams} from "react-router"

import {TOption} from "../../components/formElements/Dropdown/types"
import {TOrderBy} from "../../components/Table/utils/shared.ts"
import {
  ASalesAssistantDeal,
  ASalesAssistantDealPlannedTaskTypes,
  ASalesAssistantDealPriorities,
} from "../../services/types.generated"
import {createSimpleContext, createStateContext} from "../../utils/context.tsx"
import {useDebouncedValue} from "../../utils/hooks"
import {TSalesAssistantDealsTableColumn} from "./utils.ts"

export type TPrioritiesOption = TOption<ASalesAssistantDealPriorities>
export type TPlannedActivitiesOption = TOption<ASalesAssistantDealPlannedTaskTypes>

export function useSalesAssistantDealsFiltering() {
  const [orderBy, setOrderBy] = React.useState<TOrderBy<TSalesAssistantDealsTableColumn>>(undefined)

  const [searchString, setSearchString] = React.useState("")
  const searchStringDebounced = useDebouncedValue(searchString)

  const [priorityFilter, setPriorityFilter] = React.useState<TPrioritiesOption[]>([])
  const priorityFilterDebounced = useDebouncedValue(priorityFilter)

  const [plannedActivityFilter, setPlannedActivityFilter] = React.useState<TPlannedActivitiesOption[]>([])
  const plannedActivityFilterDebounced = useDebouncedValue(plannedActivityFilter)

  const resetAllFilters = React.useCallback(() => {
    setOrderBy(undefined)
    setSearchString("")
    setPriorityFilter([])
    setPlannedActivityFilter([])
  }, [])

  return {
    orderBy,
    setOrderBy,
    searchString,
    searchStringDebounced,
    setSearchString,
    priorityFilter,
    setPriorityFilter,
    priorityFilterDebounced,
    plannedActivityFilter,
    setPlannedActivityFilter,
    plannedActivityFilterDebounced,
    resetAllFilters,
  }
}

export const SalesAssistantDealsFilteringContext =
  createSimpleContext<ReturnType<typeof useSalesAssistantDealsFiltering>>("deals filtering")

export const SalesAssistantDealDetailContext = createStateContext<ASalesAssistantDeal | null>("deal detail")

export const SALES_PERSON_FILTER_PARAM = "sales_person_external_id"

/**
 * Reads/writes the selected sales person (by `external_id`) from the URL search params. This is the
 * single source of truth shared by the selector in the settings section and the deals / update-center
 * lists. `undefined` means "all sales people".
 */
export function useSalesAssistantSalesPersonFilter(): [string | undefined, (next: string | undefined) => void] {
  const [params, setParams] = useSearchParams()

  const value = params.get(SALES_PERSON_FILTER_PARAM) || undefined

  const setValue = React.useCallback(
    (next: string | undefined) => {
      setParams(
        prev => {
          const search = new URLSearchParams(prev)
          if (next) {
            search.set(SALES_PERSON_FILTER_PARAM, next)
          } else {
            search.delete(SALES_PERSON_FILTER_PARAM)
          }
          return search
        },
        {replace: true}
      )
    },
    [setParams]
  )

  return [value, setValue]
}
